import React, { useState } from "react"
import { useMutation } from "@apollo/client/react"
import dayjs from "dayjs"

import {
  OrderWorkflowExecutionSearchHit,
  RetryWorkflowExecutionDocument,
  RetryWorkflowExecutionMutation,
  RetryWorkflowExecutionMutationVariables
} from "lib/types/generated/graphql"
import { useAppDispatch } from "lib/store"
import alertActions from "lib/store/services/Alert/AlertSlice"

import TableHeader from "components/Ui/Table/TableHeader"
import TableRow from "components/Ui/Table/TableRow"
import TableBlurLoading from "components/Ui/Table/TableBlurLoading"
import Status from "components/Ui/Status"
import CopyButton from "components/Ui/CopyButton"
import { WithAuth } from "components/Auth"
import { PermissionGroups } from "routes/constants"

import {
  Container,
  SectionHeader,
  TableGrid,
  EmptyState,
  HeaderActions,
  LoadMoreWrapper,
  LoadMoreButton,
  WorkflowName,
  WorkflowIdCell,
  WorkflowIdText,
  OrderIdLink,
  DateText,
  DurationText,
  TriggerBadge,
  RowActions,
  RetryButton,
  RetrySpinner
} from "./WorkflowExecutionsTable.styled"

const MIN_VALID_YEAR = 2020

function isValidDate(value: string | null | undefined): boolean {
  if (!value) return false
  const d = dayjs(value)
  return d.isValid() && d.year() >= MIN_VALID_YEAR
}

function formatDate(value: string | null | undefined): string {
  if (!isValidDate(value)) return "—"
  return dayjs(value).format("YYYY-MM-DD HH:mm:ss")
}

function formatDuration(ms: number): string {
  if (ms < 0) return "—"
  if (ms < 1000) return `${ms}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = seconds / 60
  if (minutes < 60) return `${Math.floor(minutes)}m ${Math.floor(seconds % 60)}s`
  const hours = minutes / 60
  return `${Math.floor(hours)}h ${Math.floor(minutes % 60)}m`
}

function deriveStatus(hit: OrderWorkflowExecutionSearchHit): string {
  if (hit.failed) return "Failed"
  if (hit.completed) return "Completed"
  if (hit.started) return "RUNNING"
  return "Pending"
}

function calcDuration(hit: OrderWorkflowExecutionSearchHit): string | null {
  const start = hit.created
  const end = hit.completed ?? hit.failed
  if (!isValidDate(start) || !isValidDate(end)) return null
  const ms = dayjs(end).diff(dayjs(start))
  return formatDuration(ms)
}

function formatTrigger(trigger: string): string {
  return trigger
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export type ExecutionsTableVariant = "by-order" | "by-workflow" | "failed"

type Props = {
  title: string
  hits: OrderWorkflowExecutionSearchHit[]
  total?: number
  loading: boolean
  emptyMessage: string
  variant: ExecutionsTableVariant
  expansionContent: (hit: OrderWorkflowExecutionSearchHit) => React.ReactNode
  workflowNameMap?: Map<string, string>
  workflowTriggerMap?: Map<string, string>
  onLoadMore?: () => void
  onRetryComplete?: () => void
  error?: boolean
  errorMessage?: string
  headerAction?: React.ReactNode
}

function WorkflowExecutionsTable({
  title,
  hits,
  total,
  loading,
  emptyMessage,
  variant,
  expansionContent,
  workflowNameMap,
  workflowTriggerMap,
  onLoadMore,
  onRetryComplete,
  error,
  errorMessage = "Failed to load executions",
  headerAction
}: Props) {
  const dispatch = useAppDispatch()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [retryingId, setRetryingId] = useState<string | null>(null)

  const [retryExecution] = useMutation<
    RetryWorkflowExecutionMutation,
    RetryWorkflowExecutionMutationVariables
  >(RetryWorkflowExecutionDocument, {
    onCompleted: () => {
      setRetryingId(null)
      dispatch(
        alertActions.actions.createAlert({
          message: "Execution retry started",
          type: "success"
        })
      )
      onRetryComplete?.()
    },
    onError: () => {
      setRetryingId(null)
      dispatch(
        alertActions.actions.createAlert({
          message: "Failed to retry execution",
          type: "error"
        })
      )
    }
  })

  const showWorkflow = variant === "by-order" || variant === "failed"
  const showWorkflowId = variant === "by-order"
  const showTrigger = variant === "by-order"
  const showOrderId = variant === "by-workflow" || variant === "failed"
  const endTimeLabel = variant === "failed" ? "Failed" : "Completed / Failed"

  const tableHeader = (
    <TableHeader>
      <div>Status</div>
      {showWorkflow && <div className="col-workflow-name">Workflow</div>}
      {showWorkflowId && <div className="hideOnSmallScreens">ID</div>}
      {showTrigger && <div>Trigger</div>}
      {showOrderId && <div className="hideOnSmallScreens">Order ID</div>}
      <div className="hideOnSmallScreens col-revision">Rev</div>
      <div className="hideOnSmallScreens">Created</div>
      <div className="hideOnSmallScreens">{endTimeLabel}</div>
      <div className="hideOnSmallScreens">Duration</div>
      <div>Actions</div>
    </TableHeader>
  )

  const skeletonRow = (
    <TableRow>
      <div><Status status="Pending" /></div>
      {showWorkflow && (
        <div className="col-workflow-name">
          <WorkflowName>Workflow name</WorkflowName>
        </div>
      )}
      {showWorkflowId && (
        <div className="hideOnSmallScreens">
          <WorkflowIdText>workflow-id</WorkflowIdText>
        </div>
      )}
      {showTrigger && <div><TriggerBadge>...</TriggerBadge></div>}
      {showOrderId && (
        <div className="hideOnSmallScreens">
          <OrderIdLink as="span">order-id</OrderIdLink>
        </div>
      )}
      <div className="hideOnSmallScreens col-revision"><p>v1</p></div>
      <div className="hideOnSmallScreens"><DateText>—</DateText></div>
      <div className="hideOnSmallScreens"><DateText>—</DateText></div>
      <div className="hideOnSmallScreens"><DurationText>—</DurationText></div>
      <div>—</div>
    </TableRow>
  )

  if (loading && hits.length === 0) {
    return (
      <Container>
        <SectionHeader>
          <h3>{title}</h3>
          {headerAction && <HeaderActions>{headerAction}</HeaderActions>}
        </SectionHeader>
        <TableGrid>
          <TableBlurLoading numberOfRows={3} tableHeader={tableHeader} rows={skeletonRow} />
        </TableGrid>
      </Container>
    )
  }

  return (
    <Container>
      <SectionHeader>
        <h3>{title}</h3>
        {headerAction && <HeaderActions>{headerAction}</HeaderActions>}
      </SectionHeader>
      {error && <EmptyState>{errorMessage}</EmptyState>}
      {!error && hits.length === 0 ? (
        <EmptyState>{emptyMessage}</EmptyState>
      ) : (
        <TableGrid>
          {tableHeader}
          {hits.map((hit) => {
            const status =
              variant === "failed" ? "Failed" : deriveStatus(hit)
            const duration = calcDuration(hit)
            const endTime =
              variant === "failed" ? hit.failed : hit.completed ?? hit.failed
            const isExpanded = expandedId === hit.id
            const isRetrying = retryingId === hit.id
            const workflowName =
              workflowNameMap?.get(hit.workflowId) ?? hit.workflowId
            const trigger = workflowTriggerMap?.get(hit.workflowId) ?? "—"

            return (
              <React.Fragment key={hit.id}>
                <TableRow
                  handleOnClick={() => setExpandedId(isExpanded ? null : hit.id)}
                >
                  <div>
                    <Status status={status} />
                  </div>
                  {showWorkflow && (
                    <div className="col-workflow-name">
                      <WorkflowName title={workflowName}>
                        {workflowName}
                      </WorkflowName>
                    </div>
                  )}
                  {showWorkflowId && (
                    <div className="hideOnSmallScreens">
                      <WorkflowIdCell>
                        <WorkflowIdText title={hit.workflowId}>
                          {hit.workflowId}
                        </WorkflowIdText>
                        <CopyButton string={hit.workflowId} />
                      </WorkflowIdCell>
                    </div>
                  )}
                  {showTrigger && (
                    <div>
                      <TriggerBadge>{formatTrigger(trigger)}</TriggerBadge>
                    </div>
                  )}
                  {showOrderId && (
                    <div className="hideOnSmallScreens">
                      <OrderIdLink
                        href={`/orders/${hit.orderId}`}
                        onClick={(e) => e.stopPropagation()}
                        title={hit.orderId}
                      >
                        {hit.orderId}
                      </OrderIdLink>
                    </div>
                  )}
                  <div className="hideOnSmallScreens col-revision">
                    <p>v{hit.workflowRevision}</p>
                  </div>
                  <div className="hideOnSmallScreens">
                    <DateText>{formatDate(hit.created)}</DateText>
                  </div>
                  <div className="hideOnSmallScreens">
                    <DateText>{formatDate(endTime)}</DateText>
                  </div>
                  <div className="hideOnSmallScreens">
                    <DurationText>{duration ?? "—"}</DurationText>
                  </div>
                  <div>
                    {status === "Failed" ? (
                      <RowActions>
                        <WithAuth requiredGroups={PermissionGroups.workflows.write}>
                          <RetryButton
                            type="button"
                            disabled={isRetrying}
                            onClick={(e) => {
                              e.stopPropagation()
                              setRetryingId(hit.id)
                              retryExecution({
                                variables: { executionId: hit.id }
                              })
                            }}
                          >
                            {isRetrying && <RetrySpinner />}
                            {isRetrying ? "Retrying..." : "Retry"}
                          </RetryButton>
                        </WithAuth>
                      </RowActions>
                    ) : (
                      "—"
                    )}
                  </div>
                </TableRow>
                {isExpanded && expansionContent(hit)}
              </React.Fragment>
            )
          })}
          {onLoadMore && total !== undefined && total > hits.length && (
            <LoadMoreWrapper>
              <LoadMoreButton onClick={onLoadMore}>
                Load more ({total - hits.length} remaining)
              </LoadMoreButton>
            </LoadMoreWrapper>
          )}
        </TableGrid>
      )}
    </Container>
  )
}

export default WorkflowExecutionsTable
